/**
 * ATS Import Tab Component
 * Bulk import users from Excel/CSV files
 */

import { useState, useRef } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { FileSpreadsheet, Upload, Download, Loader2 } from 'lucide-react';

const ATSImportTab = ({ api }) => {
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResults, setImportResults] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileUpload = async () => {
        if (!importFile) {
            toast.error('Please select a file');
            return;
        }
        
        setImporting(true);
        setImportResults(null);
        
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            
            const response = await api.post('/ats/bulk-import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setImportResults(response.data);
            setImportFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            toast.success(`Import complete: ${response.data.summary.created} created, ${response.data.summary.updated} updated`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    const downloadTemplate = () => {
        const csv = 'email,name,tek_id,location,shift,ats_role,experience,department\njohn@example.com,John Doe,TK001,Hyderabad,Day,Recruiter,2 years,IT Staffing';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'user_import_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            <div className="p-4 rounded-lg border space-y-4">
                <div>
                    <Label className="text-base font-semibold flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4" />
                        Bulk Import from Excel/CSV
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                        Import users from a spreadsheet. Required columns: 
                        <code className="px-1 py-0.5 rounded bg-muted">email</code>, 
                        <code className="px-1 py-0.5 rounded bg-muted">name</code>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                        Optional columns: 
                        <code className="px-1 py-0.5 rounded bg-muted">tek_id</code>, 
                        <code className="px-1 py-0.5 rounded bg-muted">location</code>, 
                        <code className="px-1 py-0.5 rounded bg-muted">shift</code>, 
                        <code className="px-1 py-0.5 rounded bg-muted">ats_role</code>, 
                        <code className="px-1 py-0.5 rounded bg-muted">experience</code>, 
                        <code className="px-1 py-0.5 rounded bg-muted">department</code>
                    </p>
                </div>
                
                <div className="flex items-center gap-4">
                    <Input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                        className="flex-1"
                        data-testid="ats-file-upload"
                    />
                    <Button 
                        onClick={handleFileUpload} 
                        disabled={importing || !importFile} 
                        data-testid="ats-import-btn"
                    >
                        {importing ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Upload className="h-4 w-4 mr-2" />
                        )}
                        Import
                    </Button>
                </div>
                
                <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="ats-download-template-btn">
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                </Button>
            </div>

            {/* Import Results */}
            {importResults && (
                <div className="p-4 rounded-lg border space-y-4">
                    <Label className="text-base font-semibold">Import Results</Label>
                    
                    <div className="grid grid-cols-4 gap-4 text-center">
                        <div className="p-3 rounded-lg bg-muted/30">
                            <p className="text-2xl font-bold">{importResults.total_rows}</p>
                            <p className="text-xs text-muted-foreground">Total Rows</p>
                        </div>
                        <div className="p-3 rounded-lg bg-emerald-500/10">
                            <p className="text-2xl font-bold text-emerald-600">{importResults.summary.created}</p>
                            <p className="text-xs text-muted-foreground">Created</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-500/10">
                            <p className="text-2xl font-bold text-blue-600">{importResults.summary.updated}</p>
                            <p className="text-xs text-muted-foreground">Updated</p>
                        </div>
                        <div className="p-3 rounded-lg bg-red-500/10">
                            <p className="text-2xl font-bold text-red-600">{importResults.summary.errors}</p>
                            <p className="text-xs text-muted-foreground">Errors</p>
                        </div>
                    </div>
                    
                    {importResults.results.errors?.length > 0 && (
                        <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/30">
                            <p className="font-semibold text-red-600 text-sm mb-2">Errors:</p>
                            <ul className="space-y-1 text-sm">
                                {importResults.results.errors.slice(0, 5).map((err, idx) => (
                                    <li key={`import-error-${idx}`}>
                                        • {err.error} (Row: {JSON.stringify(err.row).substring(0, 50)}...)
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ATSImportTab;
